'use client';

import * as React from 'react';
import { ArrowUp, FileText, Mic, Paperclip, Square, X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * A user-attached file (image/doc/audio/video) staged in the composer. `base64`
 * is raw (no `data:` prefix) so transports can forward it verbatim; `previewUrl`
 * is a full data/URL used only for the inline thumbnail. Either `base64` (local
 * upload) or `url` (remote) is set.
 */
export interface Attachment {
  id: string;
  kind: 'image' | 'video' | 'audio' | 'file';
  name: string;
  mimeType?: string;
  /** Raw base64 (no `data:` prefix) — local uploads / pasted images. */
  base64?: string;
  /** Remote URL — URL-mode attachments. */
  url?: string;
  /** Full data/URL for the image thumbnail (not sent). */
  previewUrl?: string;
}

/** Default file-picker accept list (mirrors the backend's supported MIME hint). */
const DEFAULT_ACCEPT = 'image/*,application/pdf,text/plain,text/markdown';
const DEFAULT_MAX_ATTACHMENTS = 10;
const DEFAULT_MAX_BYTES = 25 * 1024 * 1024; // backend rejects inline > 25MB

/** MIME allow-list mirrored from the langbridge storage contract. */
const SUPPORTED_MIME = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/markdown',
];

export interface ComposerProps {
  placeholder?: string;
  onSend?: (text: string, attachments?: Attachment[]) => void;
  disabled?: boolean;
  /**
   * A run is streaming. The send button turns into a stop button (always
   * clickable) that calls `onStop`; no separate abort control is needed.
   */
  streaming?: boolean;
  /** Called when the stop button is pressed while `streaming`. */
  onStop?: () => void;
  /**
   * Enable file attachments (picker + paste + drag-drop). Gated by the agent's
   * multimodal capability (`io.input.acceptsAttachments`). When false, the attach
   * button/paste/drop are all off. Default false.
   */
  canAttach?: boolean;
  /**
   * `'file'` (default) — local upload via picker/paste/drop, sent as inline
   * base64. `'url'` — remote URL prompt only (workflow agents with an
   * `image_url` multimodal key that only accept URLs).
   */
  attachMode?: 'file' | 'url';
  /** File-picker `accept` attribute (from the agent's supported MIME types). */
  accept?: string;
  /** Max staged attachments (default 10, backend limit). */
  maxAttachments?: number;
  /** Max per-file size in bytes for inline base64 (default 25MB, backend limit). */
  maxBytes?: number;
  showVoice?: boolean;
  className?: string;
}

const iconBtn =
  'flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-fg-muted hover:text-fg-secondary';

function kindFromMime(mime: string): Attachment['kind'] {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

let seq = 0;
const nextId = () => `att-${Date.now()}-${seq++}`;

/** Chat input row: auto-grow textarea + send, with optional file attachments. */
export function Composer({
  placeholder = '메시지를 입력하세요…',
  onSend,
  disabled = false,
  streaming = false,
  onStop,
  canAttach = false,
  attachMode = 'file',
  accept = DEFAULT_ACCEPT,
  maxAttachments = DEFAULT_MAX_ATTACHMENTS,
  maxBytes = DEFAULT_MAX_BYTES,
  showVoice = false,
  className,
}: ComposerProps) {
  const [text, setText] = React.useState('');
  const [attachments, setAttachments] = React.useState<Attachment[]>([]);
  const [attachError, setAttachError] = React.useState<string | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const ref = React.useRef<HTMLTextAreaElement>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const dragDepth = React.useRef(0);

  const canSend = (text.trim().length > 0 || attachments.length > 0) && !disabled;
  const canStop = streaming && !!onStop;

  const addFiles = React.useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return;
      const list = Array.from(files);
      const maxMb = Math.round(maxBytes / (1024 * 1024));
      // Validate up-front (type/size/capacity) and surface a visible reason —
      // don't silently drop files. Server re-validates on upload.
      let slots = maxAttachments - attachments.length;
      const errors: string[] = [];
      const accepted: File[] = [];
      for (const file of list) {
        if (slots <= 0) {
          errors.push(`첨부는 최대 ${maxAttachments}개까지 가능합니다.`);
          break;
        }
        const mime = (file.type || 'application/octet-stream').toLowerCase();
        if (!SUPPORTED_MIME.includes(mime)) {
          errors.push(`지원하지 않는 형식입니다: ${file.name || mime}`);
          continue;
        }
        if (file.size > maxBytes) {
          errors.push(`용량 초과 (최대 ${maxMb}MB): ${file.name}`);
          continue;
        }
        accepted.push(file);
        slots -= 1;
      }
      setAttachError(errors[0] ?? null);

      accepted.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = String(reader.result || '');
          const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
          const mime = file.type || 'application/octet-stream';
          const kind = kindFromMime(mime);
          setAttachments((prev) => {
            if (prev.length >= maxAttachments) return prev;
            return [
              ...prev,
              {
                id: nextId(),
                kind,
                name: file.name || `${kind}.${mime.split('/')[1] ?? 'bin'}`,
                mimeType: mime,
                base64,
                previewUrl: kind === 'image' ? dataUrl : undefined,
              },
            ];
          });
        };
        reader.readAsDataURL(file);
      });
    },
    [attachments.length, maxAttachments, maxBytes],
  );

  const addUrl = React.useCallback(() => {
    const url = window.prompt('이미지 URL을 입력하세요');
    if (!url?.trim()) return;
    const clean = url.trim();
    setAttachments((prev) =>
      prev.length >= maxAttachments
        ? prev
        : [
            ...prev,
            {
              id: nextId(),
              kind: 'image',
              name: clean.split('/').pop() || 'image',
              url: clean,
              previewUrl: clean,
            },
          ],
    );
  }, [maxAttachments]);

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    setAttachError(null);
  };

  const onAttachClick = () => {
    if (attachMode === 'url') addUrl();
    else fileRef.current?.click();
  };

  const send = () => {
    if (!canSend) return;
    onSend?.(text.trim(), attachments.length ? attachments : undefined);
    setText('');
    setAttachments([]);
    setAttachError(null);
    if (ref.current) ref.current.style.height = 'auto';
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const grow = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  // Clipboard paste of images (Cmd/Ctrl+V). Only intercepts when files are
  // present so normal text paste still works.
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach || attachMode !== 'file') return;
    const files = Array.from(e.clipboardData?.items ?? [])
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => !!f);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const dropEnabled = canAttach && attachMode === 'file' && !disabled;
  const onDragEnter = (e: React.DragEvent) => {
    if (!dropEnabled) return;
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!dropEnabled) return;
    if (Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault();
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!dropEnabled) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  };
  const onDrop = (e: React.DragEvent) => {
    if (!dropEnabled) return;
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={cn('relative border-t border-border bg-surface', className)}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {attachError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 px-3 pt-[var(--space-2-5)] text-xs text-danger"
        >
          <span className="min-w-0 truncate">{attachError}</span>
          <button
            type="button"
            onClick={() => setAttachError(null)}
            aria-label="알림 닫기"
            className="shrink-0 text-fg-muted hover:text-fg"
          >
            <X className="size-3.5" strokeWidth={2.5} />
          </button>
        </div>
      ) : null}

      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-3 pt-[var(--space-2-5)]">
          {attachments.map((a) => (
            <AttachmentChip key={a.id} att={a} onRemove={() => removeAttachment(a.id)} />
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2 px-3 py-[var(--space-2-5)]">
        {canAttach && attachMode === 'file' ? (
          <input
            ref={fileRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        ) : null}

        <div className="flex min-h-[var(--composer-min-h)] flex-1 items-end gap-1 rounded-[var(--composer-radius)] bg-muted py-[var(--space-1)] pl-[var(--space-1-5)] pr-3">
          {canAttach ? (
            <button
              type="button"
              className={iconBtn}
              aria-label="첨부"
              onClick={onAttachClick}
              disabled={disabled}
            >
              <Paperclip className="size-[19px]" />
            </button>
          ) : null}
          <textarea
            ref={ref}
            rows={1}
            value={text}
            onChange={grow}
            onKeyDown={onKey}
            onPaste={onPaste}
            placeholder={placeholder}
            disabled={disabled}
            className="max-h-[120px] flex-1 resize-none border-none bg-transparent py-[calc(var(--space-2-5)-var(--space-1))] pl-[var(--space-1)] font-sans text-base leading-snug text-fg outline-none placeholder:text-fg-muted"
          />
        </div>

        {showVoice && !canSend && !canStop ? (
          <button type="button" className={iconBtn} aria-label="음성">
            <Mic className="size-[19px]" />
          </button>
        ) : null}

        {canStop ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="중단"
            className={cn(
              'flex size-[38px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-accent text-accent-fg',
              'transition-[background-color,transform] duration-[var(--duration-fast)] active:scale-90',
            )}
          >
            <Square className="size-[14px]" fill="currentColor" strokeWidth={0} />
          </button>
        ) : (
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            aria-label="전송"
            className={cn(
              'flex size-[38px] shrink-0 items-center justify-center rounded-full border-none',
              'transition-[background-color,transform] duration-[var(--duration-fast)] active:scale-90',
              canSend
                ? 'cursor-pointer bg-accent text-accent-fg'
                : 'cursor-not-allowed bg-muted text-fg-muted',
            )}
          >
            <ArrowUp className="size-[17px]" strokeWidth={2.2} />
          </button>
        )}
      </div>

      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-accent bg-accent-soft/80 text-sm font-semibold text-accent">
          여기에 놓아 첨부
        </div>
      ) : null}
    </div>
  );
}

/** Staged attachment chip: image thumbnail or file icon + name, with remove. */
function AttachmentChip({ att, onRemove }: { att: Attachment; onRemove: () => void }) {
  if (att.kind === 'image' && att.previewUrl) {
    return (
      <div className="relative size-[52px] overflow-hidden rounded-[var(--radius-md)] border border-border bg-muted">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={att.previewUrl} alt={att.name} className="size-full object-cover" />
        <RemoveBtn onRemove={onRemove} />
      </div>
    );
  }
  return (
    <div className="relative flex h-[52px] max-w-[180px] items-center gap-2 rounded-[var(--radius-md)] border border-border bg-muted pl-2 pr-6">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-surface text-fg-muted">
        <FileText className="size-4" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="block truncate text-2xs font-medium text-fg">{att.name}</span>
        {att.mimeType ? (
          <span className="block truncate text-[length:var(--text-2xs)] text-fg-muted">
            {att.mimeType}
          </span>
        ) : null}
      </span>
      <RemoveBtn onRemove={onRemove} />
    </div>
  );
}

function RemoveBtn({ onRemove }: { onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label="첨부 제거"
      className="absolute right-1 top-1 flex size-[18px] items-center justify-center rounded-full border border-border bg-surface text-fg-muted shadow-sm hover:text-fg"
    >
      <X className="size-3" strokeWidth={2.5} />
    </button>
  );
}
